import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CornerDownLeft, Folder, HardHat, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as RD from '@radix-ui/react-dialog';
import { api, type ApiEnvelope } from '@/lib/api-client';
import { FileIcon } from '@/components/shared/file-icon';
import { formatBytes } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Paleta de comandos (⌘K) — tarea 5.4
 *
 * Es la forma más rápida de llegar a un documento y, en una obra, la única
 * viable: nadie recuerda en qué carpeta dejó el plano de forjado, pero todo el
 * mundo recuerda un trozo de su nombre.
 *
 * TRES DECISIONES QUE LA HACEN ÚTIL:
 *
 *  · SE BUSCA MIENTRAS SE ESCRIBE, con 200 ms de espera. Sin espera, teclear
 *    «forjado» lanza siete búsquedas y las respuestas llegan desordenadas. Con
 *    más de 300 ms deja de parecer instantáneo.
 *  · LOS RESULTADOS ANTERIORES SE QUEDAN mientras llega lo nuevo
 *    (`keepPreviousData`). Vaciar la lista en cada tecla produce un parpadeo
 *    que marea y hace pensar que no hay resultados.
 *  · SE NAVEGA CON EL TECLADO. Quien abre esto con ⌘K no va a coger el ratón
 *    para elegir el primer resultado.
 */

interface Resultado {
  id: string;
  tipo: 'archivo' | 'carpeta' | 'obra';
  nombre: string;
  extension: string | null;
  contexto: string;
  projectId: string;
  sizeBytes: number | null;
}

const ETIQUETA: Record<Resultado['tipo'], string> = {
  obra: 'Obras',
  carpeta: 'Carpetas',
  archivo: 'Documentos',
};

/** El orden en que se agrupan, de lo más general a lo más concreto. */
const ORDEN: Resultado['tipo'][] = ['obra', 'carpeta', 'archivo'];

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [texto, setTexto] = useState('');
  const [consulta, setConsulta] = useState('');
  const [activo, setActivo] = useState(0);
  const navigate = useNavigate();
  const listaRef = useRef<HTMLDivElement>(null);

  // Espera de 200 ms: ni se lanza una búsqueda por tecla ni se nota el retraso.
  useEffect(() => {
    const t = setTimeout(() => setConsulta(texto.trim()), 200);
    return () => clearTimeout(t);
  }, [texto]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', consulta],
    queryFn: () =>
      api.get<ApiEnvelope<Resultado[]> & { meta: { ms: number } }>(
        `/search?q=${encodeURIComponent(consulta)}&limite=20`,
      ),
    enabled: open && consulta.length >= 2,
    // Los resultados viejos se quedan mientras llegan los nuevos: sin esto, la
    // lista parpadea en cada tecla.
    placeholderData: keepPreviousData,
  });

  const resultados = data?.data ?? [];

  // Se aplana en el orden de los grupos para que las flechas recorran lo mismo
  // que se está viendo.
  const planos = ORDEN.flatMap((tipo) => resultados.filter((r) => r.tipo === tipo));

  useEffect(() => setActivo(0), [consulta]);

  useEffect(() => {
    if (!open) {
      setTexto('');
      setConsulta('');
    }
  }, [open]);

  const ir = (r: Resultado) => {
    onOpenChange(false);
    // Todo lleva a la obra: un archivo suelto fuera de su contexto no dice
    // nada, y lo que la persona quiere es llegar a donde vive.
    navigate(`/obras/${r.projectId}`);
  };

  const alPulsarTecla = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActivo((i) => Math.min(planos.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActivo((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && planos[activo]) {
      e.preventDefault();
      ir(planos[activo]);
    }
  };

  useEffect(() => {
    listaRef.current
      ?.querySelector(`[data-indice="${activo}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activo]);

  let indice = -1;

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="anim-overlay fixed inset-0 z-50 bg-overlay backdrop-blur-[2px]" />
        <RD.Content className="anim-content fixed left-1/2 top-24 z-50 w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-floating">
          {/* UN SOLO NOMBRE ACCESIBLE. Antes había también un `aria-label` con
              otro texto, y era código muerto que además engañaba al leerlo:
              `aria-labelledby` —que Radix pone apuntando a este título— GANA
              siempre sobre `aria-label`, así que el nombre real era el de
              aquí y el otro no lo leía nadie. */}
          <RD.Title className="sr-only">Buscador global</RD.Title>

          <div className="flex items-center gap-3 border-b border-border px-4">
            <Search className="size-4 shrink-0 text-fg-subtle" />
            <input
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={alPulsarTecla}
              placeholder="Buscar obras, carpetas y documentos…"
              aria-label="Buscar"
              className="h-14 min-w-0 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-subtle"
            />
            {isFetching && <span className="text-xs text-fg-subtle">buscando…</span>}
          </div>

          <div ref={listaRef} className="max-h-[60vh] overflow-y-auto p-2">
            {consulta.length < 2 && (
              <p className="px-3 py-8 text-center text-sm text-fg-muted">
                Escribe al menos dos letras. Sirve un trozo del nombre:{' '}
                <span className="font-mono text-fg">PLANT</span> encuentra PLANTA_BAJA_R3.pdf.
              </p>
            )}

            {consulta.length >= 2 && planos.length === 0 && !isFetching && (
              <p className="px-3 py-8 text-center text-sm text-fg-muted">
                Nada coincide con «{consulta}».
              </p>
            )}

            {ORDEN.map((tipo) => {
              const grupo = resultados.filter((r) => r.tipo === tipo);
              if (grupo.length === 0) return null;

              return (
                <div key={tipo} className="mb-2">
                  <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {ETIQUETA[tipo]}
                  </p>
                  {grupo.map((r) => {
                    indice++;
                    const i = indice;
                    return (
                      <button
                        key={`${r.tipo}-${r.id}`}
                        data-indice={i}
                        onMouseEnter={() => setActivo(i)}
                        onClick={() => ir(r)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
                          i === activo ? 'bg-brand-soft/50' : 'hover:bg-surface-2',
                        )}
                      >
                        {r.tipo === 'obra' ? (
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-3 text-fg-muted">
                            <HardHat className="size-4.5" />
                          </span>
                        ) : r.tipo === 'carpeta' ? (
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-3 text-fg-muted">
                            <Folder className="size-4.5" />
                          </span>
                        ) : (
                          <FileIcon extension={r.extension} />
                        )}

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-fg">
                            {r.nombre}
                          </span>
                          <span className="block truncate text-xs text-fg-muted">
                            {r.contexto}
                            {r.sizeBytes !== null && ` · ${formatBytes(r.sizeBytes)}`}
                          </span>
                        </span>

                        {i === activo && (
                          <CornerDownLeft className="size-3.5 shrink-0 text-fg-subtle" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* El tiempo se enseña porque «18 resultados en 41 ms» da confianza en
              que el buscador ha mirado de verdad y no se ha rendido. */}
          {planos.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-fg-subtle">
              <span>
                {planos.length} {planos.length === 1 ? 'resultado' : 'resultados'}
                {data?.meta?.ms !== undefined && ` en ${data.meta.ms} ms`}
                {/* La paleta pide 20. Si vienen 20 justos, es muy probable que
                    haya más y que se estén quedando fuera: se dice, en vez de
                    dejar creer que eso es todo lo que hay. */}
                {planos.length >= 20 && ' o más'}
              </span>
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/buscar?q=${encodeURIComponent(consulta)}`);
                }}
                className="font-medium text-brand hover:underline"
              >
                Ver todos los resultados
              </button>
            </div>
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
